/*
  NOVAPIX STORE-SPECIFIC — buy box pricing + layout module.

  WHY THIS EXISTS
  Shrine's quantity-break widget cannot price a tier whose panels have different
  sizes. `QuantityBreaks.handleSelectChange` records each per-unit variant id for
  the ADD-TO-CART payload, but the displayed tier price is baked in Liquid as
  `price | times: quantity` (snippets/quantity-breaks.liquid:57). Separately, the
  `.dynamic-price` refresh is driven by <variant-selects>, which only the
  variant_picker block renders — and that block was removed when the buy box was
  collapsed to a single selector. Result: frozen prices.

  WHAT IT OWNS
  Display, layout and add-on copy only. Shrine still owns the cart payload, so
  what gets added to the cart is unchanged and untouched by this file.

  Because <variant-selects> is absent, nothing else writes to .dynamic-price and
  this module is the sole author of those numbers — no fighting a section swap.

  Remove this file and its loader block to fall back to stock Shrine behaviour.
*/
(function () {
  'use strict';

  var DEFAULT_SIZE = 'Standard';
  var FLASH_WINDOW_MS = 8 * 60 * 60 * 1000;
  var FLASH_KEY = 'novapix:flashSaleEndsAt';

  // Copied from snippets/checkbox-icons.liquid so the module-rendered gift row
  // carries the identical control to the Shrine upsell rows beside it.
  var CHECKBOX_SVG =
    '<svg class="checkmark-checked" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" ' +
    'fill="currentColor" width="50" height="50" aria-hidden="true">' +
    '<path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM337 209L209 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L303 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/>' +
    '</svg>' +
    '<svg class="checkmark-unchecked" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" ' +
    'fill="currentColor" width="50" height="50" aria-hidden="true">' +
    '<path d="M384 80c8.8 0 16 7.2 16 16V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V96c0-8.8 7.2-16 16-16H384zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64z"/>' +
    '</svg>';

  // Only the two paid add-ons are Shrine upsell blocks now. Both Mystery Gift
  // rows are rendered by this module, because one checkbox has to yield two
  // line items and the free set has to appear with no customer action at all.
  var ICONS = {
    'fast-charge-car-charger-one-for-four': 'bolt',
    'adhesive-m3-double-sided-adhesive-m3-tape-width-0-6cm': 'donut_small'
  };

  // Subtitle for both Mystery Gift rows. The charger and tape get theirs from
  // the block settings in templates/product.json; this one is rendered here
  // because the row is. Says nothing about the contents, deliberately: the
  // brief forbids disclosing them anywhere customer facing.
  var GIFT_DESC = 'A surprise bonus, contents vary';

  /* Add-ons bought as a pack. Shrine prints the upsell price straight from the
     variant, which is priced per unit, so the row would advertise one roll at
     $3.33 for something the cart charges $9.99 for. Multiplying by the pack
     size is not an override: it is what that row costs.

     The cart side of the same fact lives in assets/novapix-cart-rules.js as
     PACKS, keyed by variant id rather than handle. A test asserts the two
     agree. */
  var PACK_SIZES = {
    'adhesive-m3-double-sided-adhesive-m3-tape-width-0-6cm': 3
  };

  // ---------------------------------------------------------------- helpers

  function money(cents, dropTrailingZeros) {
    var v = cents / 100;
    var s = dropTrailingZeros && cents % 100 === 0 ? String(v) : v.toFixed(2);
    return currencySymbol + s;
  }

  /* "$3.33" -> 333. Integer cents, because 3.33 * 100 in binary floating point
     is 332.99999999999994 and three of those do not make $9.99. */
  function parseMoney(text) {
    var digits = String(text).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    var value = parseFloat(digits);
    return isNaN(value) ? null : Math.round(value * 100);
  }

  // Matches snippets/price.liquid — `round`, so headline, tier and cart agree.
  function savingsPercent(price, compare) {
    if (!compare || compare <= price) return 0;
    return Math.round(((compare - price) * 100) / compare);
  }

  var currencySymbol = '$';

  /* localStorage can throw on access (private mode, blocked cookies, opaque
     origin) — never let that escape and break the page. */
  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* timer still runs; it just restarts on reload */
    }
  }

  function detectCurrencySymbol(root) {
    var el = root.querySelector('.quantity-break__price span');
    var m = el && el.textContent.match(/^\s*([^\d\s]+)/);
    if (m) currencySymbol = m[1];
  }

  // ------------------------------------------------------------ the module

  function BuyBox(breaks) {
    this.breaks = breaks;
    this.variants = this.readVariants();
    if (!this.variants.length) return;

    this.bySize = {};
    for (var i = 0; i < this.variants.length; i++) {
      this.bySize[this.variants[i].options[0]] = this.variants[i];
    }

    this.tiers = Array.prototype.slice.call(
      breaks.querySelectorAll('.quantity-break')
    );
    this.radios = Array.prototype.slice.call(
      breaks.querySelectorAll('input[type="radio"][name="quantity"]')
    );

    // EVERY price block in this section, not just the first.
    // The buy box and the sticky add-to-cart bar both render through
    // snippets/price.liquid, but only the buy box passes `main_price: true`, so
    // the sticky bar's spans lack the main-price / main-comapre-price classes.
    // Targeting one element by those classes left the sticky bar frozen on the
    // first variant, showing Mini's price beside the real one.
    // Scoped to the section so the cart drawer, which must keep rendering from
    // real cart lines, is never touched.
    var section = breaks.closest('.shopify-section') || document;
    this.priceBlocks = Array.prototype.slice.call(section.querySelectorAll('.price'));
    this.addonHost = null;

    detectCurrencySymbol(breaks);
    this.prepareAddons();
    this.applyDefaultSize();
    this.bind();
    this.refreshAll();
  }

  BuyBox.prototype.readVariants = function () {
    var tag = this.breaks.querySelector('script[type="application/json"]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent) || [];
    } catch (e) {
      return [];
    }
  };

  // =====================================================================
  // THE SINGLE SOURCE OF TRUTH
  //
  // Everything below derives from the DOM here and nowhere else: the tier rows,
  // both .price blocks, data-selected-id, and the cart payload. Before this the
  // displayed price and the cart payload were two independent stores reading the
  // same selection separately, which is why the same class of bug kept coming
  // back in each new component. If you need any of these numbers, call
  // readState(). Do not recompute them.
  // =====================================================================

  /* Which variant is a given per-unit selector currently showing? */
  BuyBox.prototype.variantFor = function (item) {
    var select = item.querySelector('select');
    if (!select) return null;
    return this.bySize[select.value] || null;
  };

  /* Everything about one tier, derived from its own dropdowns.
     Price is the SUM of the individually selected variants, never unit x qty. */
  BuyBox.prototype.deriveTier = function (tierEl) {
    var items = tierEl.querySelectorAll('.quantity-break__selector-item');
    var variants = [];
    var lineItems = [];
    var price = 0;
    var compare = 0;

    for (var i = 0; i < items.length; i++) {
      var v = this.variantFor(items[i]);
      if (!v) continue;
      var p = parseInt(v.price, 10) || 0;
      variants.push(v);
      lineItems.push(String(v.id));
      price += p;
      // fall back to price so a variant without a compare-at never inflates savings
      compare += parseInt(v.compare_at_price, 10) || p;
    }

    return {
      el: tierEl,
      input: tierEl.dataset.input,
      items: items,
      variants: variants,
      quantity: lineItems.length,
      lineItems: lineItems,
      price: price,
      compare: compare,
      savingsPercent: savingsPercent(price, compare)
    };
  };

  /* The whole widget's state. `selected` is keyed off the CHECKED radio and
     nothing else, which is the specific thing that was going wrong: Shrine sets
     its payload from whichever tier's dropdown changed last, so editing a tier
     you were not buying silently rewrote the order. */
  BuyBox.prototype.readState = function () {
    var derived = [];
    for (var i = 0; i < this.tiers.length; i++) {
      derived.push(this.deriveTier(this.tiers[i]));
    }

    var selectedIndex = 0;
    for (var r = 0; r < this.radios.length; r++) {
      if (this.radios[r].checked) {
        selectedIndex = r;
        break;
      }
    }

    return {
      tiers: derived,
      selectedIndex: selectedIndex,
      selected: derived[selectedIndex] || derived[0] || null
    };
  };

  /* Push the derived state into Shrine's own fields.

     ProductForm builds items[n][id] from quantityBreaks.formVariants, and Shrine
     writes that field from two places (its select handler and its radio handler,
     last write wins). Overwriting it here makes the payload a projection of the
     state that rendered the prices rather than a second, independently-derived
     store, so the two cannot drift apart. */
  BuyBox.prototype.pushToCartState = function (state) {
    var qb = this.breaks;
    if (!state || !state.selected) return;

    if (qb.selectedVariants) {
      for (var i = 0; i < state.tiers.length; i++) {
        var t = state.tiers[i];
        if (t.input) qb.selectedVariants[t.input] = t.lineItems.slice();
      }
    }
    qb.formVariants = state.selected.lineItems.slice();
    qb.selectedQuantity = state.selected.quantity;
  };

  // ------------------------------------------------------------- rendering

  /* Render one tier row from its already-derived state. Also mirrors each
     per-unit choice onto data-selected-id, which Shrine's initVariants() reads
     when rebuilding its own arrays. */
  BuyBox.prototype.renderTier = function (t) {
    var tier = t.el;
    var onSale = t.compare > t.price;

    for (var n = 0; n < t.items.length; n++) {
      if (t.variants[n]) t.items[n].dataset.selectedId = t.variants[n].id;
    }

    var priceEl = tier.querySelector('.quantity-break__price span');
    if (priceEl) priceEl.textContent = money(t.price, false);

    var compareWrap = tier.querySelector('.quantity-break__compare-price');
    var compareEl = compareWrap && compareWrap.querySelector('span');
    if (compareEl) compareEl.textContent = money(t.compare, false);
    if (compareWrap) compareWrap.style.display = onSale ? '' : 'none';

    var benefit = tier.querySelector('.quantity-break__benefit');
    if (benefit) {
      if (onSale) {
        benefit.style.display = '';
        var amount = benefit.querySelector('span');
        if (amount) {
          amount.textContent = money(t.compare - t.price, true);
        } else {
          benefit.textContent = 'SAVE ' + money(t.compare - t.price, true);
        }
      } else {
        benefit.style.display = 'none';
      }
    }
    return t;
  };

  /* Write the selected tier's total into one price block.

     Uses only selectors that exist in BOTH renderings of snippets/price.liquid:
     the buy box (main_price: true) and the sticky bar (no main_price). Notably
     the compare-at is found via `.price__compare-price s`, not the
     `.main-comapre-price` class, which the sticky bar does not have. */
  BuyBox.prototype.renderPrice = function (container, totals) {
    if (!container) return;
    var onSale = totals.compare > totals.price;

    var regular = container.querySelector('.price__regular .price-item--regular');
    if (regular) regular.textContent = money(totals.price, false);

    var sale = container.querySelector('.price__sale .price-item--sale');
    if (sale) sale.textContent = money(totals.price, false);

    var compare = container.querySelector('.price__compare-price s');
    if (compare) compare.textContent = money(totals.compare, false);

    var compareWrap = container.querySelector('.price__compare-price');
    if (compareWrap) compareWrap.style.display = onSale ? '' : 'none';

    var badge = container.querySelector('.price__badge-sale');
    if (badge) {
      badge.style.display = onSale ? '' : 'none';
      var label = badge.querySelector('.nowrap');
      if (label) {
        label.textContent = 'SAVE ' + savingsPercent(totals.price, totals.compare) + '%';
      }
    }

    container.classList.toggle('price--on-sale', onSale);
  };

  /* Every price surface mirrors the selected tier's total, so the buy box and
     the sticky bar can never disagree. */
  BuyBox.prototype.renderAllPrices = function (totals) {
    for (var i = 0; i < this.priceBlocks.length; i++) {
      this.renderPrice(this.priceBlocks[i], totals);
    }
  };

  /* The one applier. Derives state once, then writes it to every surface,
     display and cart alike. Nothing else in this file recomputes these values. */
  BuyBox.prototype.refreshAll = function () {
    var state = this.readState();
    if (!state.selected) return state;

    for (var i = 0; i < state.tiers.length; i++) {
      this.renderTier(state.tiers[i]);
    }
    this.renderAllPrices(state.selected);
    this.pushToCartState(state);
    this.placeAddons(state.selected.el);
    return state;
  };

  // --------------------------------------------------------------- add-ons

  BuyBox.prototype.prepareAddons = function () {
    var containers = document.querySelectorAll('.product-info-upsells-container');
    this.addonBlocks = Array.prototype.slice.call(containers);

    var upsells = document.querySelectorAll('product-info-upsell[data-handle]');
    for (var i = 0; i < upsells.length; i++) {
      this.decorateUpsell(upsells[i]);
    }

    // The paid Mystery Gift is no longer a Shrine upsell block. One checkbox has
    // to produce TWO line items, which product_upsell cannot express, so the row
    // is rendered here and the cart effect is handled by the reconcile rule in
    // novapix-cart-rules.js. It is still one row, never two options.
    this.paidGiftRow = document.createElement('div');
    this.paidGiftRow.className = 'novapix-gift-paid';
    this.paidGiftRow.dataset.selected = 'false';
    // Mirrors Shrine's upsell markup element for element (icon, content with a
    // title row carrying the price, description, checkbox) so the shared CSS
    // in novapix-buy-box.css styles both renderers with the same rules.
    this.paidGiftRow.innerHTML =
      '<button type="button" class="novapix-gift-paid__container">' +
      '<span class="novapix-addon-icon">' +
      '<span class="material-icon material-symbols-outlined">redeem</span></span>' +
      '<span class="novapix-gift-paid__content">' +
      '<span class="novapix-gift-paid__head">' +
      '<span class="novapix-gift-paid__label">Mystery Gift</span>' +
      '<span class="novapix-gift-paid__price">$20.00</span>' +
      '</span>' +
      // Says nothing about the contents, per the brief.
      '<span class="novapix-gift-paid__desc">' + GIFT_DESC + '</span>' +
      '</span>' +
      // Same square SVG checkbox the charger and tape rows use (snippets/
      // checkbox-icons.liquid, the checkbox_1 style), so all three rows match.
      '<span class="upsell__checkbox novapix-gift-paid__check flex-center">' +
      CHECKBOX_SVG +
      '</span>' +
      '</button>';

    var self = this;
    this.paidGiftRow.querySelector('.novapix-gift-paid__container')
      .addEventListener('click', function () {
        var on = self.paidGiftRow.dataset.selected !== 'true';
        self.paidGiftRow.dataset.selected = on ? 'true' : 'false';
        // The cart is only ever written by the one rule.
        if (window.NovapixCart) window.NovapixCart.setPaidIntent(on);
      });

    // If reconcile decides a deleted piece means they opted out, untick here.
    if (window.NovapixCart && window.NovapixCart.onPaidCleared) {
      window.NovapixCart.onPaidCleared(function () {
        self.paidGiftRow.dataset.selected = 'false';
      });
    }

    // Locked and genuinely non-interactive: no input, no toggle, nothing to click.
    this.giftRow = document.createElement('div');
    this.giftRow.className = 'novapix-gift-included';
    this.giftRow.setAttribute('aria-label', 'Mystery Gift included free');
    this.giftRow.setAttribute('aria-disabled', 'true');
    this.giftRow.innerHTML =
      '<span class="novapix-gift-included__icon">' +
      '<span class="material-icon material-symbols-outlined">redeem</span></span>' +
      '<span class="novapix-gift-included__content">' +
      '<span class="novapix-gift-included__text">Mystery Gift Included</span>' +
      '<span class="novapix-gift-included__desc">' + GIFT_DESC + '</span>' +
      '</span>' +
      '<span class="novapix-gift-included__free">FREE</span>';
  };

  /* Did the customer ask for the paid set? Only meaningful on the 1 panel tier. */
  BuyBox.prototype.paidGiftRequested = function () {
    return !!this.paidGiftRow && this.paidGiftRow.dataset.selected === 'true';
  };

  /* Swap the product photo for an icon. The title is left alone: every row
     carries a checkbox, so a "+ Add" prefix only added noise and pushed the
     three labels out of alignment with each other. */
  BuyBox.prototype.decorateUpsell = function (upsell) {
    var handle = upsell.dataset.handle;

    var container = upsell.querySelector('.upsell__container');
    if (container && !upsell.querySelector('.novapix-addon-icon')) {
      var icon = document.createElement('span');
      icon.className = 'novapix-addon-icon';
      icon.innerHTML =
        '<span class="material-icon material-symbols-outlined">' +
        (ICONS[handle] || 'add_circle') +
        '</span>';

      var oldImage = upsell.querySelector('.upsell__image');
      if (oldImage) {
        oldImage.parentNode.replaceChild(icon, oldImage);
      } else {
        container.insertBefore(icon, container.firstChild);
      }
    }

    var titleEl = upsell.querySelector('.upsell__title h3');
    if (titleEl) {
      // Collapse the whitespace the Liquid template leaves around the name.
      // Guarded because this function also runs from a MutationObserver on
      // document.body: writing textContent unconditionally replaces the text
      // node, which is itself a childList mutation, which calls this again.
      var trimmed = titleEl.textContent.trim();
      if (titleEl.textContent !== trimmed) titleEl.textContent = trimmed;
    }

    this.showPackPrice(upsell, handle);
  };

  /* A pack sold add-on shows the price of the pack, not of one unit.

     The data flag is load bearing, not tidiness: this runs from a
     MutationObserver on document.body, so without it the write would be seen
     as a change and trigger another write forever. Stamping an attribute is an
     attribute mutation, which that observer does not watch. Shrine replacing
     the row on a variant change brings a fresh node with no flag, so it gets
     decorated again, which is what we want. */
  BuyBox.prototype.showPackPrice = function (upsell, handle) {
    var packSize = PACK_SIZES[handle];
    if (!packSize) return;

    var priceEl = upsell.querySelector('.upsell__price .regular-price') ||
      upsell.querySelector('.upsell__price');
    if (!priceEl || priceEl.dataset.novapixPack) return;

    var unit = parseMoney(priceEl.textContent);
    if (unit === null) return;

    priceEl.dataset.novapixPack = String(packSize);
    priceEl.textContent = money(unit * packSize);
  };

  /* Move (never clone) the upsell blocks under the selected tier.
     Cloning would duplicate the elements Shrine reads for the cart payload. */
  BuyBox.prototype.placeAddons = function (tier) {
    if (!tier) return;

    if (!this.addonHost) {
      this.addonHost = document.createElement('div');
      this.addonHost.className = 'novapix-tier-addons';
    }

    // sits AFTER the label: .quantity-break is a <label for=...>, so anything
    // inside it would have its clicks swallowed by the radio toggle.
    if (tier.nextSibling !== this.addonHost) {
      tier.parentNode.insertBefore(this.addonHost, tier.nextSibling);
    }

    var qty = parseInt(tier.dataset.quantity, 10) || 1;
    var giftIsFree = qty >= 2;

    // Exactly one of the two rows is ever in the DOM, so the paid option simply
    // does not exist on a 2+ tier. The cart rule enforces the same thing again
    // server-side, independently of the UI.
    if (giftIsFree) {
      if (this.paidGiftRow.parentNode) {
        this.paidGiftRow.parentNode.removeChild(this.paidGiftRow);
      }
      // Moving up a tier cancels the paid choice outright, in the UI and in the
      // rule, so a pair added earlier cannot survive alongside the free set.
      if (this.paidGiftRow.dataset.selected === 'true' && window.NovapixCart) {
        window.NovapixCart.setPaidIntent(false);
      }
      this.paidGiftRow.dataset.selected = 'false';
      if (this.giftRow.parentNode !== this.addonHost) {
        this.addonHost.insertBefore(this.giftRow, this.addonHost.firstChild);
      }
    } else {
      if (this.giftRow.parentNode) {
        this.giftRow.parentNode.removeChild(this.giftRow);
      }
      if (this.paidGiftRow.parentNode !== this.addonHost) {
        this.addonHost.insertBefore(this.paidGiftRow, this.addonHost.firstChild);
      }
    }

    for (var i = 0; i < this.addonBlocks.length; i++) {
      if (this.addonBlocks[i].parentNode !== this.addonHost) {
        this.addonHost.appendChild(this.addonBlocks[i]);
      }
    }
  };

  // -------------------------------------------------------------- defaults

  BuyBox.prototype.applyDefaultSize = function () {
    if (!this.bySize[DEFAULT_SIZE]) return;
    var selects = this.breaks.querySelectorAll('.quantity-break__variant-select');

    for (var i = 0; i < selects.length; i++) {
      if (selects[i].value === DEFAULT_SIZE) continue;
      selects[i].value = DEFAULT_SIZE;
      // bubbles so Shrine's own listener on .quantity-break__selector-item
      // records the variant id for the cart payload.
      selects[i].dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  BuyBox.prototype.bind = function () {
    var self = this;

    this.breaks.addEventListener('change', function (event) {
      var t = event.target;
      if (!t) return;
      if (t.classList && t.classList.contains('quantity-break__variant-select')) {
        self.refreshAll();
      } else if (t.name === 'quantity') {
        self.refreshAll();
      }
    });

    // Submit-time guard, capture phase.
    //
    // Capture runs before Shrine's own bubble-phase form handler, so the payload
    // is re-derived from the CHECKED tier at the moment of submit. This is the
    // safety net for the whole class of bug: even if some future component
    // writes formVariants behind our back, the value that actually reaches
    // /cart/add is re-asserted here first.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.querySelector) return;
        if (!self.breaks.isConnected) return;
        // only the product form for this section
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;
        self.pushToCartState(self.readState());

        // Record the choice, do not try to time the cart write. The flag is
        // sticky, so whenever the reconcile triggered by Shrine's add lands, it
        // already knows the answer. Scheduling the write here instead raced the
        // still-in-flight panel add and silently dropped the gift.
        if (window.NovapixCart) {
          window.NovapixCart.setPaidIntent(
            self.paidGiftRequested() && self.readState().selected.quantity === 1
          );
        }
      },
      true
    );

    // Shrine re-renders upsells on some interactions; re-apply decoration.
    new MutationObserver(function () {
      var upsells = document.querySelectorAll('product-info-upsell[data-handle]');
      for (var i = 0; i < upsells.length; i++) self.decorateUpsell(upsells[i]);
    }).observe(document.body, { childList: true, subtree: true });
  };

  // ------------------------------------------------------------- countdown

  function segment(unit, label) {
    return (
      '<span class="novapix-countdown__seg" data-unit="' + unit + '">' +
      '<span class="novapix-countdown__num">--</span>' +
      '<span class="novapix-countdown__unit">' + label + '</span>' +
      '</span>'
    );
  }

  function startCountdown() {
    var price = document.querySelector('.price');
    if (!price || document.querySelector('.novapix-countdown')) return;

    // Reads are guarded too, not just writes: touching localStorage throws
    // outright in blocked-cookie / opaque-origin contexts, which would take the
    // countdown down with an uncaught error.
    var endsAt = parseInt(storageGet(FLASH_KEY), 10);
    if (!endsAt || isNaN(endsAt) || endsAt <= Date.now()) {
      endsAt = Date.now() + FLASH_WINDOW_MS;
      storageSet(FLASH_KEY, String(endsAt));
    }

    // Three discrete segments rather than one string, so each unit reads as its
    // own block. Only the number nodes are rewritten on tick; the unit labels are
    // static, which keeps the DOM churn to three text nodes a second.
    var bar = document.createElement('div');
    bar.className = 'novapix-countdown';
    bar.innerHTML =
      '<span class="novapix-countdown__label">⚡ Flash Sale Ends In</span>' +
      '<span class="novapix-countdown__clock">' +
      segment('hrs', 'HRS') +
      '<span class="novapix-countdown__sep" aria-hidden="true">:</span>' +
      segment('min', 'MIN') +
      '<span class="novapix-countdown__sep" aria-hidden="true">:</span>' +
      segment('sec', 'SEC') +
      '</span>';

    var host = price.closest('.product__info-block') || price.parentNode;
    host.parentNode.insertBefore(bar, host.nextSibling);

    var out = {
      hrs: bar.querySelector('[data-unit="hrs"] .novapix-countdown__num'),
      min: bar.querySelector('[data-unit="min"] .novapix-countdown__num'),
      sec: bar.querySelector('[data-unit="sec"] .novapix-countdown__num')
    };

    function pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    function tick() {
      var left = endsAt - Date.now();
      if (left <= 0) {
        endsAt = Date.now() + FLASH_WINDOW_MS;
        storageSet(FLASH_KEY, String(endsAt));
        left = FLASH_WINDOW_MS;
      }
      var s = Math.floor(left / 1000);
      out.hrs.textContent = pad(Math.floor(s / 3600));
      out.min.textContent = pad(Math.floor((s % 3600) / 60));
      out.sec.textContent = pad(s % 60);
    }

    tick();
    window.setInterval(tick, 1000);
  }

  // ------------------------------------------------------------------ init

  function init() {
    var breaks = document.querySelector('quantity-breaks');
    // Single-variant buy boxes (Matrix Mask template) render no per-unit
    // selector items. Constructing BuyBox there would sum zero selected
    // variants: $0.00 painted over every Liquid-rendered price, an empty
    // cart payload re-asserted at submit, and gift rows injected on a
    // product with no gift program. Liquid already renders correct tier
    // prices on that template, so it gets the countdown only.
    var hasSelectorItems = breaks && breaks.querySelector('.quantity-break__selector-item');
    if (hasSelectorItems && !breaks.dataset.novapixReady) {
      breaks.dataset.novapixReady = 'true';
      new BuyBox(breaks);
    }
    startCountdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
